import { useState } from "react";
import {
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarTrigger,
} from "@/components/ui/menubar";
import { useTranslation } from "react-i18next";
import {
  Send,
  User,
  CheckCircle,
  FileText,
  UserCheck,
  FileCheck,
  Shield,
  Trash2,
} from "lucide-react";
import { PATH_CONTRACTOR } from "@/constants";

import { useAgreementData } from "../hooks/useAgreementData";
import { useAgreementContext } from "../context";
import { Menubar } from "@/components/ui/menubar";
import { AgreementActionDialog } from "./shared";

type AgreementDialogType =
  | "content"
  | "data"
  | "statements"
  | "taxStatus"
  | "complete";

export const AgreementMenu = () => {
  const { t } = useTranslation();
  const [dialogState, setDialogState] = useState<{
    type: AgreementDialogType | null;
    isOpen: boolean;
  }>({ type: null, isOpen: false });

  const data = useAgreementData();
  const {
    handleSendQuestionnaire,
    handleComplete,
    handleUpdateContent,
    handleUpdateData,
    handleUpdateStatements,
    handleUpdateTaxStatus,
    handleDelete,
    transitions,
  } = useAgreementContext();

  const handleGoToContractor = () => {
    const url = PATH_CONTRACTOR.replace(
      ":contractorId",
      data.contractor.contractor_id,
    );
    window.open(url, "_blank");
  };

  const openDialog = (type: AgreementDialogType) => {
    setDialogState({ type, isOpen: true });
  };

  const closeDialog = () => {
    setDialogState({ type: null, isOpen: false });
  };

  const handleConfirm = (date: string) => {
    switch (dialogState.type) {
      case "content":
        handleUpdateContent(date);
        break;
      case "data":
        handleUpdateData(date);
        break;
      case "taxStatus":
        handleUpdateTaxStatus(date);
        break;
      case "complete":
        handleComplete(date);
        break;
    }
    closeDialog();
  };

  return (
    <Menubar className="mb-2">
      <MenubarMenu>
        <MenubarTrigger>{t("agreements.menubar.trigger")}</MenubarTrigger>
        <MenubarContent>
          {transitions.canSendQuestionnaire && (
            <MenubarItem onClick={handleSendQuestionnaire}>
              <Send className="mr-2 h-4 w-4" />
              {t("agreements.menubar.sendQuestionnaire")}
            </MenubarItem>
          )}
          {transitions.canUpdateData && (
            <MenubarItem onClick={() => openDialog("data")}>
              <UserCheck className="mr-2 h-4 w-4" />
              {t("agreements.menubar.updateData")}
            </MenubarItem>
          )}
          {transitions.canUpdateTaxStatus && (
            <MenubarItem onClick={() => openDialog("taxStatus")}>
              <Shield className="mr-2 h-4 w-4" />
              {t("agreements.menubar.updateTaxStatus")}
            </MenubarItem>
          )}
          {transitions.canUpdateStatements && (
            <MenubarItem onClick={handleUpdateStatements}>
              <FileCheck className="mr-2 h-4 w-4" />
              {t("agreements.menubar.updateStatements")}
            </MenubarItem>
          )}
          {transitions.canUpdateContent && (
            <MenubarItem onClick={() => openDialog("content")}>
              <FileText className="mr-2 h-4 w-4" />
              {t("agreements.menubar.updateContent")}
            </MenubarItem>
          )}
          {transitions.canComplete && (
            <MenubarItem onClick={() => openDialog("complete")}>
              <CheckCircle className="mr-2 h-4 w-4" />
              {t("agreements.menubar.complete")}
            </MenubarItem>
          )}
          {transitions.canDelete && (
            <MenubarItem onClick={handleDelete}>
              <Trash2 className="mr-2 h-4 w-4" />
              {t("agreements.menubar.delete")}
            </MenubarItem>
          )}
          <MenubarItem onClick={handleGoToContractor}>
            <User className="mr-2 h-4 w-4" />
            {t("agreements.menubar.goToContractor")}
          </MenubarItem>
        </MenubarContent>
      </MenubarMenu>
      <AgreementActionDialog
        isOpen={dialogState.isOpen && dialogState.type !== "statements"}
        onClose={closeDialog}
        onConfirm={handleConfirm}
        title={t(`agreements.dialogs.${dialogState.type}.title`, "")}
        description={t(
          `agreements.dialogs.${dialogState.type}.description`,
          "",
        )}
        agreement={data}
      />
    </Menubar>
  );
};
